import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchVanchiNames, updateVanchiName } from '../../store/slices/mastersSlice';
import { TableColumn, SimplePaginationInfo } from '../../types';
import { mastersService } from '../../services/mastersService';
import { vanchiNameService } from '../../services/vanchiNameService';
import { ValuesetParam } from '../../common/utils/valueset';
import { toast } from '../../common/utils/toast';
import DataTable from '../../common/components/DataTable';

const AddVanchiNo: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { vanchiNames: vanchiNamesList, total, loading } = useAppSelector((state) => state.masters);
  const [nadaList, setNadaList] = useState<any[]>([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingVanchiName, setEditingVanchiName] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Validation schema
  const validationSchema = yup.object({
    nadaId: yup.string().required('Please select a Nada'),
    vanchiName: yup.string().required('Please select a Vanchi Name'),
    vanchiNumber: yup.string().when('$isEditMode', {
      is: true,
      then: (schema) => schema.required('Vanchi Number is required').min(1, 'Vanchi Number must be at least 1 character'),
      otherwise: (schema) => schema.optional()
    }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm<{nadaId: string; vanchiName: string; vanchiNumber?: string}>({
    resolver: yupResolver(validationSchema, {
      context: { isEditMode }
    }),
    defaultValues: {
      nadaId: '',
      vanchiName: '',
      vanchiNumber: '',
    },
  });

  const watchedNadaId = watch('nadaId');
  const watchedVanchiName = watch('vanchiName');
  
  // Debug: Log current form values

  // Load nada list
  const loadNada = async () => {
    try {
      const response = await mastersService.getValuesByParam(ValuesetParam.NADA);
      setNadaList(response.data || []);
    } catch (error) {
      console.error('Failed to load nada list:', error);
    }
  };

  // Load vanchi list
  const loadVanchiList = () => {
    dispatch(fetchVanchiNames());
  };

  useEffect(() => {
    loadVanchiList();
    loadNada();
  }, [dispatch]);

  // Reset vanchi name and vanchi number when nada changes (not in edit mode)
  useEffect(() => {
    if (!isEditMode && watchedNadaId) {
      // Only reset if we're not in edit mode and nada is selected
      setValue('vanchiName', '');
      setValue('vanchiNumber', '');
    }
  }, [watchedNadaId, isEditMode, setValue]);

  // Filter vanchi names based on selected nada
  const filteredVanchiNames = vanchiNamesList.filter(vanchiName => 
    (vanchiName as any).nadacd === watchedNadaId
  );

  // Auto-populate vanchi number when vanchi is selected (only in edit mode)
  useEffect(() => {
    if (watchedVanchiName && isEditMode && editingVanchiName) {
      const vanchiNo = (editingVanchiName as any).vanchino;
      if (!vanchiNo || vanchiNo === '' || vanchiNo === '-') {
        setValue('vanchiNumber', '');
      } else {
        setValue('vanchiNumber', vanchiNo);
      }
    }
  }, [watchedVanchiName, isEditMode, editingVanchiName, setValue]);
  
  // Debug: Log filtered vanchi names

  const handleEditClick = (vanchiName: any) => {
    // Set edit mode first
    setIsEditMode(true);
    setEditingVanchiName(vanchiName);
    
    // Use a more robust approach with multiple attempts
    const setFormValues = () => {
      setValue('nadaId', (vanchiName as any).nadacd, { shouldValidate: true });
      setValue('vanchiName', (vanchiName as any).vanchiname, { shouldValidate: true });
      setValue('vanchiNumber', (vanchiName as any).vanchino || '', { shouldValidate: true });
      
      // Trigger validation to ensure form state is updated
      trigger();
    };
    
    // Set values immediately
    setFormValues();
    
    // Also try setting values after a short delay to ensure they stick
    setTimeout(setFormValues, 50);
    setTimeout(setFormValues, 100);
  };


  // Determine placeholder text based on mode
  const getPlaceholderText = () => {
    return 'Enter Vanchi Number';
  };

  // Determine page title based on mode
  const getPageTitle = () => {
    if (isEditMode) {
      return 'UPDATE VANCHI';
    }
    return 'CREATE VANCHI';
  };

  const handleReset = () => {
    reset({ nadaId: '', vanchiName: '', vanchiNumber: '' });
    setIsEditMode(false);
    setEditingVanchiName(null);
  };

  // Create new vanchi
  const handleCreate = async (data: {nadaId: string; vanchiName: string; vanchiNumber?: string}) => {
    try {
      // Find the nada name
      const selectedNada = nadaList.find(nada => nada.valuesetcode === data.nadaId);
      if (!selectedNada) {
        toast.showError('Please select a valid Nada');
        return;
      }

      // Generate vanchicd (you might need to adjust this logic based on your requirements)
      const vanchicd = `VANCHI${Date.now()}`;

      const payload = {
        nadacd: data.nadaId,
        nadaname: selectedNada.valuesetnameen || selectedNada.valuesetnameml,
        vanchicd: vanchicd,
        vanchiname: data.vanchiName,
        vanchino: data.vanchiNumber || '-',
        isactive: 'Y',
        cretedby: user?.userid || 'SYSTEM'
      };

      await vanchiNameService.createVanchiName(payload);
      toast.showSuccess('Vanchi created successfully!');
      
      handleReset();
      loadVanchiList(); // Refresh the vanchi list
    } catch (err) {
      toast.showError('Failed to create vanchi');
    }
  };

  // Edit existing vanchi
  const handleEdit = async (data: {nadaId: string; vanchiName: string; vanchiNumber?: string}) => {
    try {
      if (!editingVanchiName) {
        toast.showError('Please select a vanchi to update');
        return;
      }

      // Update existing vanchi name with vanchi number
      await dispatch(updateVanchiName({ 
        id: (editingVanchiName as any).tblid, 
        data: { nadaId: data.nadaId, vanchiName: data.vanchiName, vanchiNumber: data.vanchiNumber } as any, 
        existingRowData: editingVanchiName, 
        nadaList, 
        userId: user?.userid,
        isVanchiNoForm: true // Flag to indicate this is from vanchi no form
      })).unwrap();
      toast.showSuccess('Vanchi updated successfully!');
      
      handleReset();
      loadVanchiList(); // Refresh the vanchi list
    } catch (err) {
      toast.showError('Failed to update vanchi');
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      }) + ' / ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '-';
    }
  };

  const columns: TableColumn[] = [
    {
      key: 'sNo',
      label: 'S.No.',
      sortable: true,
      width: 50,
      render: (_value: any, row: any) => {
        const index = vanchiNamesList.findIndex(vanchiName => (vanchiName as any).tblid === (row as any).tblid);
        return String(index + 1).padStart(2, '0');
      }
    },
    {
      key: 'nadaname',
      label: 'Nada Name',
      sortable: true,
      width: 120,
    },
    {
      key: 'vanchiname',
      label: 'Vanchi Name',
      sortable: true,
      width: 120,
    },
    {
      key: 'vanchino',
      label: 'Vanchi No',
      sortable: true,
      width: 90,
    },
    {
      key: 'createddt',
      label: 'Creation Date/Time',
      sortable: true,
      width: 160,
      render: (value: string) => formatDateTime(value)
    },
    {
      key: 'cretedby',
      label: 'Created By',
      sortable: true,
      width: 80,
    },
    {
      key: 'edit',
      label: 'Edit',
      sortable: false,
      width: 50,
      isEditColumn: true,
      onEditClick: handleEditClick,
    }
  ];

  const pagination: SimplePaginationInfo = {
    page: currentPage,
    limit: 10,
  };

  return (
    <div className="h-100">
      <div className="row h-100">
        {/* Left Pane - Form (45%) */}
        <div className="col-lg-5 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-header bg-primary text-secondary">
              <h6 className="mb-0">{getPageTitle()}</h6>
            </div>
            <div className="card-body d-flex flex-column h-100">
              <div className="d-flex flex-column h-100">
                {/* Form Content - Flexible */}
                <div className="">
                  {/* Select Nada Field */}
                  <div className="mb-2">
                    <label className="form-label text-primary">SELECT NADA *</label>
                    <select
                      className={`form-select border-primary ${errors.nadaId ? 'is-invalid' : ''}`}
                      {...register('nadaId')}
                    >
                      <option value="">Select Nada</option>
                      {nadaList.map((nada) => (
                        <option key={nada.valuesetcode} value={nada.valuesetcode}>
                          {nada.valuesetnameen || nada.valuesetnameml}
                        </option>
                      ))}
                    </select>
                    {errors.nadaId && (
                      <div className="invalid-feedback">{errors.nadaId.message}</div>
                    )}
                  </div>

                  {/* Select Vanchi Field */}
                  <div className="mb-2">
                    <label className="form-label text-primary">SELECT VANCHI *</label>
                    <select
                      className={`form-select border-primary ${errors.vanchiName ? 'is-invalid' : ''}`}
                      {...register('vanchiName')}
                      disabled={!watchedNadaId}
                    >
                      <option value="">Select Vanchi Name</option>
                      {filteredVanchiNames.map((vanchiName) => (
                        <option key={(vanchiName as any).tblid} value={(vanchiName as any).vanchiname}>
                          {(vanchiName as any).vanchiname}
                        </option>
                      ))}
                    </select>
                    {errors.vanchiName && (
                      <div className="invalid-feedback">{errors.vanchiName.message}</div>
                    )}
                  </div>

                  {/* Vanchi Number Field */}
                  <div className="mb-4">
                    <label className="form-label text-primary">VANCHI NUMBER *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.vanchiNumber ? 'is-invalid' : ''}`}
                      placeholder={getPlaceholderText()}
                      {...register('vanchiNumber')}
                    />
                    {errors.vanchiNumber && (
                      <div className="invalid-feedback">{errors.vanchiNumber.message}</div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - Fixed at Bottom */}
                <div className="d-flex justify-content-end">
                  <div className="d-flex gap-2">
                    {!isEditMode && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit(handleCreate)}
                      >
                        CREATE VANCHI
                      </button>
                    )}

                    {isEditMode && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit(handleEdit)}
                      >
                        UPDATE VANCHI
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane - Table (55%) */}
        <div className="col-lg-7 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={vanchiNamesList}
                loading={loading}
                pagination={pagination}
                totalCountOverride={total}
                onPageChange={setCurrentPage}
                onSort={() => { }} // Sorting handled by Redux thunk in real app
                searchable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVanchiNo;